import { intersection } from 'lodash-es'
import { isWithinInterval } from '@/utils/dates'
import { useAuthStore } from '@/ContextTab/stores/auth/auth'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import {
  ENewsEventType,
  type ENewsEventTypeValues,
  ENewsLinkReverse,
  ENewsStatus,
  type ENewsStatusReverse,
} from '@/lib/references'
import { ACCESS } from '@/lib/accessCodes'
import { usePublishedLocalFiltersStore } from '@/ContextTab/stores/newsSet/published/localFilters'
import { useGroupsAndUsersStore } from '@/ContextTab/stores/groupsAndUsers'
import { usePublishedFiltersStore } from '@/ContextTab/stores/newsSet/published/filters'
import type { TaskPanelNewsSetType } from '@/lib/types'
import type { NewsItemPublished } from '@/ContextTab/stores/types'
import type { NewsVersionEventType } from '@/api/generated/v2'


// Здесь собраны предикаты из следующих мест (до 2025-03):
// src/lib/setPredicates/news.ts
// src/ContextApp/stores/news/task-panel/predicates.ts
// ContextApp/stores/news/published/set

export type TNewsPredicate = (newsItem: NewsItemPublished) => boolean | Promise<boolean>

export function usePredicates() {

  const authStore = useAuthStore()
  const userProfileStore = useUserProfileStore()
  const groupsAndUsersStore = useGroupsAndUsersStore()
  const publishedLocalFiltersStore = usePublishedLocalFiltersStore()
  const publishedFilterStore = usePublishedFiltersStore()


  // Проверяет, находится ли новость в одном из статусов
  async function checkNewsStatus(
    statusNames: (keyof typeof ENewsStatusReverse)[],
    newsItem: NewsItemPublished,
  ) {
    const statusId = newsItem?.version?.status_id
    return (
      statusId
        ? statusNames.includes(ENewsStatus[statusId])
        : false
    )
  }

  async function checkByHideRepublicationsFilter(newsItem: NewsItemPublished) {
    return publishedLocalFiltersStore.hideRepublications
      ? (
          newsItem.parent_rel_type_id !== ENewsLinkReverse.REPUBLICATION
          && newsItem.parent_rel_type_id !== ENewsLinkReverse.REPUBLICATION_UNEDITED
        )
      : true
  }

  // Если у пользователя есть привязка `can_work` или пользователь в группе
  function checkUserCanWork(newsItem: NewsItemPublished, canWorkIds: (string | number)[]) {
    return (
      !!canWorkIds.find((userId) => authStore.userId === userId)
      || userProfileStore.getIsUserInGroups(newsItem.groups ?? [])
    )
  }

  // Проверка того, работал ли пользователь над содержанием новости
  // https://jira.interfax.ru/browse/PUB-3764
  function checkUserIsContributor(newsItem: NewsItemPublished) {
    const userId = authStore.userId
    return (
      userId
        ? newsItem?.users?.find(({ user }) => user?.id === userId)?.is_contributor ?? false
        : false
    )
  }

  function checkIsFromPS(newsItem: NewsItemPublished) {
    return newsItem.origin_id === null
  }

  async function checkByCodesFilter(newsItem: NewsItemPublished) {
    const selectedCodeIds = publishedFilterStore.selectedNonFeedCodeIds
    const versionCodes = newsItem.version?.codes ?? []

    return (
      selectedCodeIds.length === 0
      || versionCodes.some(({ code }) => selectedCodeIds.includes(code.id))
    )
  }

  async function checkByFeedFilter(newsItem: NewsItemPublished) {
    const selectedCodeIds = publishedFilterStore.selectedFeedCodeIds
    const versionCodes = (
      // special filtration for republished/unedited (PUB-3470)
      newsItem.parent_rel_type_id === ENewsLinkReverse.REPUBLICATION_UNEDITED
        ? newsItem.version?.codes?.filter((code) => code.is_added_by_republication_unedited)
        : newsItem.version?.codes
    ) ?? []

    return (
      selectedCodeIds.length === 0
      || versionCodes.some(({ code }) => selectedCodeIds.includes(code.id))
    )
  }

  async function checkByUsersFilter(newsItem: NewsItemPublished) {
    if (!publishedFilterStore.userFiltersForPredicates.length) {
      return true
    }

    const newsItemVersion = newsItem.version
    if (!newsItem || !newsItemVersion) {
      return false
    }

    type FilterState = {
      ids: string[]
      excludedIds: string[]
      ignoredIds: string[]
    }
    const initialState: FilterState = { ids: [], excludedIds: [], ignoredIds: [] }

    const addItemsToAccumulatorByState = (
      accumulator: any,
      name: string,
      id: string,
      state: any,
    ) => {
      if (/G$/g.test(id)) {
        const groupChildren = groupsAndUsersStore.groupAndUsersById[id]?.children
        if (!groupChildren) {
          return
        }
        groupChildren.forEach((child: any) => {
          const isInValues = publishedFilterStore.userFiltersForPredicates.find(
            (item: any) => item.id === child,
          )

          if (isInValues && isInValues.state !== state) {
            return
          }
          accumulator[name].push(child)
        })
      } else {
        accumulator[name].push(id)
      }
    }

    const processedValues: FilterState = publishedFilterStore.userFiltersForPredicates.reduce(
      (accumulator: FilterState, current: any) => {
        if (current.state === 'included') {
          addItemsToAccumulatorByState(accumulator, 'ids', current.id, 'included')
        } else if (current.state === 'excluded') {
          addItemsToAccumulatorByState(
            accumulator,
            'excludedIds',
            current.id,
            'excluded',
          )
        } else if (current.state === 'ignored') {
          addItemsToAccumulatorByState(
            accumulator,
            'ignoredIds',
            current.id,
            'ignored',
          )
        }

        return accumulator
      },
      initialState,
    )

    const authors = newsItemVersion.authors?.map((author: any) => author.user.id) ?? []
    const publisher = newsItem.publisher?.id
    const users = [...authors, ...(publisher
      ? [publisher]
      : [])]
    const hasExcluded = intersection(processedValues.excludedIds, users).length

    if (hasExcluded) {
      return false
    } else if (
      processedValues.ids.length
      && !intersection(processedValues.ids, users).length
    ) {
      return false
    }
    return true
  }

  function checkByDatesFilter(newsItem: NewsItemPublished) {
    if (
      !publishedLocalFiltersStore.dates?.start
      || !publishedLocalFiltersStore.dates?.end
      || !newsItem.status_modified_at
    ) {
      return true
    }

    return isWithinInterval(
      new Date(newsItem.status_modified_at),
      {
        start: publishedLocalFiltersStore.dates.start,
        end: publishedLocalFiltersStore.dates.end,
      },
    )
  }

  function checkIsInUserGroups(newsItem: NewsItemPublished) {
    const filterGroups = userProfileStore.profile.filters?.news_waiting?.groups?.ids ?? []
    const filterUsers = userProfileStore.profile.filters?.news_waiting?.users?.ids ?? []

    const canWorkIds = (newsItem?.users ?? [])
      .filter((user) => user.can_work)
      .map((user) => user.user?.id)
      .filter((id) => typeof id === 'number')

    if (filterGroups?.length || filterUsers?.length) {
      let isInGroups = false
      let isToMe = false

      if (filterGroups?.length) {
        isInGroups = Boolean(newsItem.groups?.length)
          && (newsItem.groups ?? [])
            .map((link) => link.group?.id)
            .filter((id) => typeof id === 'number')
            .reduce((acc, id) => acc || filterGroups.includes(id), false)
      }

      if (filterUsers?.length) {
        isToMe = Boolean(
          filterUsers.find((id: number) => canWorkIds.includes(id)),
        )
      }

      return isInGroups || isToMe
    } else {
      return checkUserCanWork(newsItem, canWorkIds)
    }
  }

  function checkIsMine(newsItem: NewsItemPublished) {
    return (
      authStore.userId
      && newsItem.version?.user?.id === authStore.userId
    )
  }

  function checkIsUserNewsOrUserHasRightToSeeIt(newsItem: NewsItemPublished) {
    const isUserNews = checkIsMine(newsItem)

    const permissions = authStore.tokenData?.permissions ?? []
    const userHasPermission = permissions.includes(ACCESS.NEWS.GROUPMATES_NEWS)

    const newsCurrentUserGroups = newsItem.version?.user?.group_ids || []
    const isUserInGroups = userProfileStore.getIsUserInGroups(
      newsCurrentUserGroups.map((id: number) => ({ group: { id } })),
    )
    return isUserNews || (userHasPermission && isUserInGroups)
  }

  // function, not related with newsItem, but used for same purposes
  function checkByNewsEvent(excludeEvents: ENewsEventTypeValues, typeId: NewsVersionEventType) {
    const eventName = ENewsEventType[typeId]
    return !excludeEvents.includes(eventName)
  }

  const publishedPredicates = [
    checkNewsStatus.bind(null, ['PUBLISHED']),
    checkByHideRepublicationsFilter.bind(null),
    checkByDatesFilter.bind(null),
    checkByUsersFilter.bind(null),
    checkByCodesFilter.bind(null),
    checkByFeedFilter.bind(null),
    // checkByNewsEvent also should be checked, but doesn't depend on newsItem: extracted
  ]

  const taskPanelPredicates: Record<TaskPanelNewsSetType, TNewsPredicate[]> = {
    waiting: [
      checkNewsStatus.bind(null, ['WAITS_FOR_EDIT']),
      checkIsInUserGroups.bind(null),
    ],
    inProgress: [
      checkNewsStatus.bind(null, ['DRAFT', 'ON_EDIT']),
      checkIsUserNewsOrUserHasRightToSeeIt.bind(null),
    ],
    my: [
      checkUserIsContributor.bind(null),
      checkIsFromPS.bind(null),
    ],
  }

  async function checkAgainstPredicates(
    predicates: TNewsPredicate[],
    newsItem: NewsItemPublished,
  ): Promise<boolean> {
    for await (const predicate of predicates) {
      if (!(await predicate(newsItem))) {
        return false
      }
    }
    return true
  }

  return {
    publishedPredicates,
    taskPanelPredicates,
    checkAgainstPredicates,
    checkByNewsEvent,
    checkIsMine,
  }
}
